// src/components/ChatWidget/ChatWidget.tsx
// InvestMenter Chat Widget - ChatGPT-style conversation history
// Each open/close cycle = one conversation; past chats stored in Messages tab

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { OfferFacts, offerFactLines } from './offerFactsText';
import {
  X,
  Home,
  MessageSquare,
  Send,
  ChevronRight,
  ArrowLeft,
  Search,
  FileText,
  CreditCard,
  Building2,
  Globe,
  Key,
  Briefcase,
  PaintBucket,
  Shield,
  Plus,
  Mic,
  Square,
  Play,
  Pause,
} from 'lucide-react';
import './ChatWidget.css';
import { appLanguage } from '../../i18n/locale';

// ============================================================
// TYPES
// ============================================================

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent' | 'advisor' | 'system';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
  audioUrl?: string;
  audioDuration?: number;
  audioMimeType?: string;
  senderName?: string;
  type?: 'inactivity_warning' | 'auto_close' | string;
  /** Dubai Sell units the InvestMenter Brain offered in this reply. */
  offers?: ChatOffer[];
  /** Asks real estate vs property management after a handoff; set once answered. */
  topicChoice?: 'ask' | 'realestate' | 'pm';
}

interface ChatOffer {
  id: string;
  title: string | null;
  propertyType?: string | null;
  location: string | null;
  sizeSqft: number | null;
  bedrooms: number | null;
  askingPrice: number | null;
  isDistress?: boolean;
  discountPercent?: number | null;
  photoUrl: string | null;
  /** Real DLD evidence from the server (services/offerFacts.js). */
  facts?: OfferFacts;
}

interface Conversation {
  id: string;
  startedAt: string; // ISO string
  messages: Message[];
}

interface FAQItem {
  id: string;
  icon: React.ReactNode;
  titleEN: string;
  titleDE: string;
}

interface ChatWidgetProps {
  investorName?: string;
  investorEmail?: string;
  investorPhone?: string;
  language?: 'en' | 'de';
  backendUrl?: string;
  isOpen?: boolean;
  onToggle?: () => void;
  initialMessage?: string;
  /** 'brain': the initial message opens a fresh InvestMenter Brain conversation. */
  initialMode?: 'brain';
  onInitialMessageConsumed?: () => void;
}

// ============================================================
// HELPERS
// ============================================================

/** initialMessage that opens a Brain conversation without sending anything. */
export const BRAIN_START = '__brain_start__';

// Strip internal action/tag annotations from text (used for previews and display)
const stripActions = (text: string) =>
  text.replace(/\[(ACTION|INTERNAL):[^\]]*?\]?/g, '').replace(/\s{2,}/g, ' ').trim();

const CONV_STORAGE_KEY = (email: string) => `cw_conversations_${email}`;
const MAX_STORED_CONVERSATIONS = 20;

function loadConversations(email: string): Conversation[] {
  if (!email) return [];
  try {
    const raw = localStorage.getItem(CONV_STORAGE_KEY(email));
    if (!raw) {
      // Migrate old single-chat format if present
      const legacy = localStorage.getItem(`cw_messages_${email}`);
      if (legacy) {
        const msgs = JSON.parse(legacy).map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
        if (msgs.length > 0) {
          const legacyConv: Conversation = {
            id: `conv_legacy_${Date.now()}`,
            startedAt: msgs[0].timestamp instanceof Date
              ? msgs[0].timestamp.toISOString()
              : new Date(msgs[0].timestamp).toISOString(),
            messages: msgs,
          };
          localStorage.removeItem(`cw_messages_${email}`);
          return [legacyConv];
        }
      }
      return [];
    }
    const parsed: Conversation[] = JSON.parse(raw);
    return parsed.map(c => ({
      ...c,
      messages: c.messages.map(m => ({ ...m, timestamp: new Date(m.timestamp) })),
    }));
  } catch {
    return [];
  }
}

function saveConversations(email: string, convs: Conversation[]) {
  if (!email) return;
  try {
    // Keep only the most recent MAX_STORED_CONVERSATIONS
    const toSave = convs.slice(-MAX_STORED_CONVERSATIONS);
    localStorage.setItem(CONV_STORAGE_KEY(email), JSON.stringify(toSave));
  } catch {}
}

// ============================================================
// FAQ DATA
// ============================================================

const FAQ_ITEMS: FAQItem[] = [
  { id: 'payment-plan', icon: <CreditCard size={18} />, titleEN: 'Payment Plans', titleDE: 'Zahlungspläne' },
  { id: 'oqood', icon: <FileText size={18} />, titleEN: 'OQOOD Registration', titleDE: 'OQOOD Registrierung' },
  { id: 'spa', icon: <Building2 size={18} />, titleEN: 'SPA (Purchase Agreement)', titleDE: 'SPA (Kaufvertrag)' },
  { id: 'golden-visa', icon: <Globe size={18} />, titleEN: 'Golden Visa', titleDE: 'Golden Visa' },
  { id: 'kyc', icon: <Key size={18} />, titleEN: 'KYC Process', titleDE: 'KYC Prozess' },
  { id: 'furnishing', icon: <PaintBucket size={18} />, titleEN: 'Furnishing & Handover', titleDE: 'Möblierung & Übergabe' },
  { id: 'mortgage', icon: <Shield size={18} />, titleEN: '50% Mortgage Financing', titleDE: '50% Hypothekenfinanzierung' },
  { id: 'bank-account', icon: <Briefcase size={18} />, titleEN: 'UAE Bank Account', titleDE: 'UAE Bankkonto' },
  { id: 'taxes', icon: <CreditCard size={18} />, titleEN: 'Taxes & Fees', titleDE: 'Steuern & Gebühren' },
  { id: 'rental', icon: <Building2 size={18} />, titleEN: 'Rental Strategy', titleDE: 'Vermietungsstrategie' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

const ChatWidget: React.FC<ChatWidgetProps> = ({
  investorName = '',
  investorEmail = '',
  investorPhone = '',
  language = 'en',
  backendUrl = '',
  isOpen = false,
  onToggle,
  initialMessage,
  initialMode,
  onInitialMessageConsumed,
}) => {
  const [currentView, setCurrentView] = useState<'home' | 'chat' | 'conv-view'>('home');
  const [activeTab, setActiveTab] = useState<'home' | 'messages'>('home');

  // Current live chat messages (cleared each new session)
  const [messages, setMessages] = useState<Message[]>([]);

  // All past conversations
  const [conversations, setConversations] = useState<Conversation[]>(() =>
    loadConversations(investorEmail)
  );

  // When viewing a past conversation (read-only)
  const [viewingConv, setViewingConv] = useState<Conversation | null>(null);

  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIsOpenRef = useRef(isOpen);
  const currentConvIdRef = useRef<string>(`conv_${Date.now()}`);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialMessageConsumedRef = useRef(false);
  // Which conversation the backend runs: the menu assistant, or the
  // InvestMenter Brain advisor. Set when the Brain opens the chat, cleared by
  // a new chat. A ref, because handleSend runs from timers set before a render.
  const chatModeRef = useRef<'brain' | undefined>(undefined);
  // The Brain opens with a language choice (English / Deutsch) shown here,
  // without a server call; the answer sets the language for the conversation.
  const brainLangPendingRef = useRef(false);
  const chatLangRef = useRef<'en' | 'de' | undefined>(undefined);

  // WhatsApp-style hold-to-record refs
  const touchStartXRef = useRef<number>(0);
  const slideCancelledRef = useRef(false);
  const recordingStartTimeRef = useRef<number>(0);
  const [slideOffset, setSlideOffset] = useState(0);
  const isRecordingRef = useRef(false); // sync ref for event listeners

  // ── Handoff state (live advisor chat) ──
  const [handoffActive, setHandoffActive] = useState(false);
  const handoffPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastPollTimestampRef = useRef<string | null>(null);

  const isDE = language === 'de';
  const firstName = investorName?.split(' ')[0] || '';

  // ── Archive current chat when widget CLOSES ──────────────────
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (wasOpen && !isOpen) {
      // Widget is closing — archive current messages if any
      if (messages.length > 0) {
        const conv: Conversation = {
          id: currentConvIdRef.current,
          startedAt: messages[0].timestamp instanceof Date
            ? messages[0].timestamp.toISOString()
            : new Date(messages[0].timestamp).toISOString(),
          messages,
        };
        setConversations(prev => {
          const updated = [...prev, conv];
          saveConversations(investorEmail, updated);
          return updated;
        });
        setMessages([]);
        currentConvIdRef.current = `conv_${Date.now()}`;
      }
      // End any active handoff when widget closes
      if (handoffActive) {
        setHandoffActive(false);
        lastPollTimestampRef.current = null;
      }
      // Reset to home view
      setCurrentView('home');
      setActiveTab('home');
      setViewingConv(null);
    }
  }, [isOpen, messages, investorEmail]);

  // ── Persist conversations on change ──────────────────────────
  useEffect(() => {
    saveConversations(investorEmail, conversations);
  }, [conversations, investorEmail]);

  // ── Inactivity follow-up (10 min) then auto-close (5 min later) ─
  const followUpSentRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    followUpSentRef.current = false;

    inactivityTimerRef.current = setTimeout(() => {
      setMessages(prev => {
        if (prev.length === 0) return prev;
        const followUp: Message = {
          id: `agent_followup_${Date.now()}`,
          text: isDE
            ? 'Möchten Sie fortfahren oder kann ich Ihnen noch bei etwas anderem helfen? Antworten Sie einfach oder ich beende die Unterhaltung. 😊'
            : "Would you like to continue or can I help you with anything else? Simply reply or I'll end the conversation. 😊",
          sender: 'agent',
          timestamp: new Date(),
        };
        return [...prev, followUp];
      });
      followUpSentRef.current = true;

      // Auto-close after 5 more minutes with no reply
      closeTimerRef.current = setTimeout(() => {
        setMessages(prev => {
          if (prev.length === 0) return prev;
          const goodbye: Message = {
            id: `agent_goodbye_${Date.now()}`,
            text: isDE
              ? 'Da keine Antwort kam, beende ich die Unterhaltung jetzt. Auf Wiedersehen! 😊'
              : "Since there's no reply, I'll end the conversation here. Goodbye! 😊",
            sender: 'agent',
            timestamp: new Date(),
          };
          return [...prev, goodbye];
        });
        setTimeout(() => onToggle?.(), 3000);
      }, 5 * 60 * 1000);
    }, 10 * 60 * 1000);
  }, [isDE, onToggle]);

  useEffect(() => {
    // Only reset the inactivity timer when user sends a message (not when agent adds a message)
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.sender === 'user') resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [messages.length, resetInactivityTimer]);

  // ── Auto-scroll ───────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ── Handoff polling: fetch advisor messages every 3s when handoff is active ──
  useEffect(() => {
    if (handoffActive && backendUrl && investorEmail && investorEmail !== 'guest') {
      // Start polling
      const poll = async () => {
        try {
          // First poll reaches back a day, so a reply the team sent before the
          // widget was reopened still arrives (duplicates are filtered by id).
          const since = lastPollTimestampRef.current || new Date(Date.now() - 24 * 3600000).toISOString();
          // The status endpoint is session-checked (requireInvestorAuth). This
          // call used to go out without the token, so every poll was a 401
          // and replies from the admin Chat panel never reached the owner.
          const authToken = localStorage.getItem('authToken');
          const res = await fetch(`${backendUrl}/handoff/status/${encodeURIComponent(investorEmail)}?since=${encodeURIComponent(since)}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          const data = await res.json();

          if (data.success && data.messages && data.messages.length > 0) {
            // Update last poll timestamp to the latest message
            const latestTs = data.messages[data.messages.length - 1].timestamp;
            lastPollTimestampRef.current = latestTs;

            // Add new messages to the chat
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const newMsgs: Message[] = data.messages
                .filter((m: any) => !existingIds.has(m.id) && m.sender !== 'investor')
                .map((m: any) => ({
                  id: m.id,
                  text: m.text,
                  sender: m.sender === 'advisor' ? 'advisor' as const : 'system' as const,
                  senderName: m.senderName || (appLanguage() === 'de' ? 'Berater' : 'Advisor'),
                  timestamp: new Date(m.timestamp),
                  type: m.type,
                }));
              return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
            });
          }

          // Check if handoff was closed (timeout or advisor ended)
          if (data.success && !data.handoffActive) {
            setHandoffActive(false);
          }
        } catch (e) {
          console.error('Handoff poll error:', e);
        }
      };

      poll(); // immediate first poll
      handoffPollRef.current = setInterval(poll, 3000);

      return () => {
        if (handoffPollRef.current) {
          clearInterval(handoffPollRef.current);
          handoffPollRef.current = null;
        }
      };
    } else {
      // Stop polling when handoff ends
      if (handoffPollRef.current) {
        clearInterval(handoffPollRef.current);
        handoffPollRef.current = null;
      }
    }
  }, [handoffActive, backendUrl, investorEmail]);

  // ── Focus input on chat open ──────────────────────────────────
  useEffect(() => {
    if (currentView === 'chat') {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [currentView]);

  // ── Auto-send initial message when opened from service card ──
  useEffect(() => {
    if (isOpen && initialMessage && !initialMessageConsumedRef.current) {
      initialMessageConsumedRef.current = true;
      const brain = initialMode === 'brain';
      if (brain) {
        archiveCurrentConversation();
        setHandoffActive(false);
        lastPollTimestampRef.current = null;
        chatModeRef.current = 'brain';
        chatLangRef.current = undefined;
      }
      // Open chat view and send the template message
      setCurrentView('chat');
      setActiveTab('home');
      setViewingConv(null);
      if (brain && initialMessage === BRAIN_START) {
        // Opened from the Brain page: nothing is sent in the investor's name.
        // The Brain greets and asks for the language first.
        startBrain();
        onInitialMessageConsumed?.();
        return;
      }
      // Small delay to let the view render, then send
      setTimeout(() => {
        handleSend(initialMessage, brain ? { fresh: true } : undefined);
        onInitialMessageConsumed?.();
      }, 300);
    }
    if (!isOpen) {
      initialMessageConsumedRef.current = false;
    }
  }, [isOpen, initialMessage]);

  // ============================================================
  // VOICE RECORDING (WhatsApp-style audio messages)
  // ============================================================
  const formatRecordingTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Helper: convert Blob to base64 string
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Strip the data:audio/webm;base64, prefix
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const getSupportedMimeType = (): string => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/aac',
      'audio/ogg;codecs=opus',
      'audio/wav',
    ];
    for (const type of types) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return ''; // Let browser pick default
  };

  const recordingMimeTypeRef = useRef<string>('audio/webm');

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = getSupportedMimeType();
      recordingMimeTypeRef.current = mimeType || 'audio/webm';

      const recorderOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Stop all tracks to release mic
        stream.getTracks().forEach(track => track.stop());

        // Don't send if cancelled via slide or too short (< 0.5s)
        const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
        if (slideCancelledRef.current || elapsed < 0.5) {
          slideCancelledRef.current = false;
          audioChunksRef.current = [];
        } else {
          const audioBlob = new Blob(audioChunksRef.current, { type: recordingMimeTypeRef.current });
          if (audioBlob.size > 0) {
            sendVoiceMessage(audioBlob, Math.max(1, Math.round(elapsed)));
          }
        }

        // Clear timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        setRecordingTime(0);
        setSlideOffset(0);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250); // Collect data in chunks for reliability
      recordingStartTimeRef.current = Date.now();
      slideCancelledRef.current = false;
      setIsRecording(true);
      isRecordingRef.current = true;
      setRecordingTime(0);
      setSlideOffset(0);

      // Start timer with auto-stop at 2 minutes
      const MAX_RECORDING_SECONDS = 120;
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= MAX_RECORDING_SECONDS) {
            stopVoiceRecording();
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      console.error('Voice recording error:', err);
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        alert(isDE ? 'Bitte erlauben Sie den Mikrofonzugriff.' : 'Please allow microphone access.');
      } else if (err?.name === 'NotSupportedError') {
        alert(isDE ? 'Sprachaufnahme wird auf diesem Gerät nicht unterstützt.' : 'Voice recording is not supported on this device.');
      } else {
        alert(isDE ? 'Sprachaufnahme fehlgeschlagen. Bitte versuchen Sie es erneut.' : 'Voice recording failed. Please try again.');
      }
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    isRecordingRef.current = false;
  };

  const cancelVoiceRecording = () => {
    slideCancelledRef.current = true;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Detach handler so it doesn't send the message, then stop
      const recorder = mediaRecorderRef.current;
      recorder.onstop = () => {
        // Release microphone
        try { recorder.stream.getTracks().forEach(track => track.stop()); } catch {}
      };
      recorder.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    isRecordingRef.current = false;
    setRecordingTime(0);
    setSlideOffset(0);
    audioChunksRef.current = [];
  };

  // ── WhatsApp-style hold-to-record handlers ──
  const handleMicTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    touchStartXRef.current = e.touches[0].clientX;
    slideCancelledRef.current = false;
    setSlideOffset(0);
    startVoiceRecording();
  };

  const handleMicMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    touchStartXRef.current = e.clientX;
    slideCancelledRef.current = false;
    setSlideOffset(0);
    startVoiceRecording();
  };

  // Global touch/mouse move for slide-to-cancel
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (!isRecordingRef.current) return;
      const dx = e.touches[0].clientX - touchStartXRef.current;
      if (dx < 0) {
        setSlideOffset(dx);
        if (dx < -100) {
          cancelVoiceRecording();
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isRecordingRef.current) return;
      const dx = e.clientX - touchStartXRef.current;
      if (dx < 0) {
        setSlideOffset(dx);
        if (dx < -100) {
          cancelVoiceRecording();
        }
      }
    };

    const handleRelease = () => {
      if (!isRecordingRef.current) return;
      stopVoiceRecording();
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleRelease);
    window.addEventListener('touchcancel', handleRelease);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleRelease);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleRelease);
      window.removeEventListener('touchcancel', handleRelease);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleRelease);
    };
  }, []);

  const sendVoiceMessage = async (audioBlob: Blob, duration: number) => {
    const localAudioUrl = URL.createObjectURL(audioBlob);
    const voiceMsgId = `user_voice_${Date.now()}`;
    const mimeType = audioBlob.type || recordingMimeTypeRef.current;

    // Show the voice message immediately with local blob URL
    const voiceMsg: Message = {
      id: voiceMsgId,
      text: isDE ? '🎤 Sprachnachricht' : '🎤 Voice message',
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
      audioUrl: localAudioUrl,
      audioDuration: duration,
      audioMimeType: mimeType,
    };
    setMessages(prev => [...prev, voiceMsg]);

    try {
      const audioBase64 = await blobToBase64(audioBlob);
      const authToken = localStorage.getItem('authToken');

      // Upload voice to backend for persistent storage
      const uploadRes = await fetch(`${backendUrl}/voice/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          audioBase64,
          duration,
          mimeType,
          investorEmail: investorEmail || '',
          investorName: investorName || 'App User',
          investorId: investorEmail || 'guest',
        }),
      });

      const uploadData = await uploadRes.json();
      // uploadData.audioUrl is "/api/voice/xxx" and backendUrl already ends with "/api"
      const baseUrl = backendUrl.replace(/\/api\/?$/, '');
      const persistentUrl = uploadData.success ? `${baseUrl}${uploadData.audioUrl}` : localAudioUrl;

      // Update message with persistent URL and sent status
      setMessages(prev =>
        prev.map(m => m.id === voiceMsgId
          ? { ...m, status: 'sent' as const, audioUrl: persistentUrl }
          : m
        )
      );

      // ── Transcribe voice and get AI response ──
      {
        setIsTyping(true);
        setIsSending(true);

        try {
          // Step 1: Transcribe the audio
          const transcribeRes = await fetch(`${backendUrl}/voice/transcribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify({
              audioBase64,
              mimeType,
              investorId: investorEmail || 'guest',
              investorName: investorName || 'App User',
              investorEmail: investorEmail || '',
            }),
          });

          const transcribeData = await transcribeRes.json();

          if (!transcribeRes.ok) {
            console.error('Transcription API error:', transcribeData);
          }

          const transcription = transcribeData.transcription || '';

          if (transcription) {
            // Update voice message text with transcription
            setMessages(prev =>
              prev.map(m => m.id === voiceMsgId
                ? { ...m, text: `🎤 "${transcription}"` }
                : m
              )
            );

            // Step 2: Send transcribed text to AI chatbot
            const historyForApi = messages.slice(-20).map(m => ({
              text: m.sender === 'agent' ? stripActions(m.text) : m.text,
              isUser: m.sender === 'user',
            }));

            const chatRes = await fetch(`${backendUrl}/chatbot`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
              },
              body: JSON.stringify({
                message: transcription,
                history: historyForApi,
                investorId: investorEmail || 'guest',
                investorName: investorName || 'App User',
                investorEmail: investorEmail || '',
                language,
                ...(chatModeRef.current ? { mode: chatModeRef.current } : {}),
              }),
            });

            const chatData = await chatRes.json();
            const agentText = chatData.message || chatData.response || (isDE
              ? 'Entschuldigung, ich konnte keine Antwort erhalten.'
              : "Sorry, I couldn't get a response.");

            setMessages(prev => [...prev, {
              id: `agent_${Date.now()}`,
              text: agentText,
              sender: 'agent',
              timestamp: new Date(),
            }]);
          } else {
            // Transcription failed - tell user
            setMessages(prev => [...prev, {
              id: `agent_${Date.now()}`,
              text: isDE
                ? 'Entschuldigung, ich konnte die Sprachnachricht nicht verstehen. Bitte versuchen Sie es erneut oder tippen Sie Ihre Nachricht.'
                : "Sorry, I couldn't understand the voice message. Please try again or type your message.",
              sender: 'agent',
              timestamp: new Date(),
            }]);
          }
        } catch (err) {
          console.error('Voice transcription/chat error:', err);
          setMessages(prev => [...prev, {
            id: `err_${Date.now()}`,
            text: isDE
              ? 'Verbindungsfehler bei der Sprachnachricht. Bitte versuchen Sie es erneut.'
              : 'Connection error processing voice message. Please try again.',
            sender: 'agent',
            timestamp: new Date(),
          }]);
        } finally {
          setIsTyping(false);
          setIsSending(false);
        }
      }
    } catch (err) {
      console.error('Voice upload error:', err);
      // Still mark as sent with local URL as fallback
      setMessages(prev =>
        prev.map(m => m.id === voiceMsgId ? { ...m, status: 'sent' as const } : m)
      );
    }
  };

  // ============================================================
  // SEND MESSAGE
  // ============================================================
  // Answer to the post-handoff question: routes the in-app thread the team
  // reads (PM console Chat) to the investor's Executive Assistant or to the
  // property management team.
  const chooseHandoffTopic = async (msgId: string, topic: 'realestate' | 'pm') => {
    const label = topic === 'realestate'
      ? (isDE ? 'Meine Immobilie / Investment' : 'My property / real estate')
      : (isDE ? 'Hausverwaltung' : 'Property management');
    setMessages(prev => prev.map(m => (m.id === msgId ? { ...m, topicChoice: topic, text: `${isDE ? 'Thema' : 'Topic'}: ${label}` } : m)));
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${backendUrl}/pm/messages/me/topic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ topic }),
      });
    } catch { /* the thread stays visible to every admin, so nothing is lost */ }
  };

  const handleSend = async (text?: string, opts?: { fresh?: boolean }) => {
    let messageText = text || inputText.trim();
    if (!messageText || isSending) return;
    // What goes to the server; differs from the bubble only for the language pick.
    let apiText = messageText;
    let fresh = !!opts?.fresh;
    if (chatModeRef.current === 'brain' && brainLangPendingRef.current) {
      brainLangPendingRef.current = false;
      const pick = /^\s*(1|en|english|englisch)\b/i.test(messageText) ? 'en'
        : /^\s*(2|de|deutsch|german)\b/i.test(messageText) ? 'de' : undefined;
      if (pick) {
        chatLangRef.current = pick;
        messageText = pick === 'de' ? 'Deutsch' : 'English';
        apiText = pick === 'de' ? 'Guten Tag' : 'Hello';
        fresh = true; // the Brain starts the conversation from here
      }
    }

    const userMsg: Message = {
      id: `user_${Date.now()}`,
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsSending(true);

    // ── Send to chatbot endpoint ──
    setIsTyping(true);

    try {
      const authToken = localStorage.getItem('authToken');
      // Pass clean history (strip action tags from agent messages so AI isn't confused)
      const historyForApi = fresh ? [] : messages.slice(-20).map(m => ({
        text: m.sender === 'agent' ? stripActions(m.text) : m.text,
        isUser: m.sender === 'user',
      }));

      const response = await fetch(`${backendUrl}/chatbot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          message: apiText,
          history: historyForApi,
          investorId: investorEmail || 'guest',
          investorName: investorName || 'App User',
          investorEmail: investorEmail || '',
          language: chatLangRef.current || language,
          ...(chatModeRef.current ? { mode: chatModeRef.current } : {}),
        }),
      });

      const data = await response.json();

      setMessages(prev =>
        prev.map(m => m.id === userMsg.id ? { ...m, status: 'sent' } : m)
      );

      const agentText = data.message || data.response || (isDE
        ? 'Entschuldigung, ich konnte keine Antwort erhalten. Bitte versuchen Sie es erneut.'
        : "Sorry, I couldn't get a response. Please try again.");

      // The server moved this conversation to the InvestMenter Brain (a menu
      // pick that is advice): stay there, in the language it chose.
      if (data.mode === 'brain') {
        chatModeRef.current = 'brain';
        if (data.language === 'de' || data.language === 'en') chatLangRef.current = data.language;
      }

      // Check if handoff was started or is active
      if (data.handoffActive) {
        setHandoffActive(true);
        lastPollTimestampRef.current = new Date().toISOString();
      }
      // Check if handoff was ended by investor
      if (data.handoffEnded) {
        setHandoffActive(false);
        lastPollTimestampRef.current = null;
      }

      const agentMsg: Message = {
        id: `agent_${Date.now()}`,
        text: agentText,
        sender: data.handoffActive ? 'system' : 'agent',
        timestamp: new Date(),
        ...(Array.isArray(data.offers) && data.offers.length ? { offers: data.offers } : {}),
      };

      setMessages(prev => [...prev, agentMsg]);

      // A handoff just began: ask what it is about, because that decides which
      // team member gets the conversation (their Executive Assistant or the
      // property management team). Only signed-in investors have a thread.
      if (data.handoffActive && !handoffActive && investorEmail && investorEmail !== 'guest') {
        setMessages(prev => [...prev, {
          id: `topic_${Date.now()}`,
          text: isDE ? 'Worum geht es, damit die richtige Person antwortet?' : 'What is this about, so the right person answers?',
          sender: 'system',
          timestamp: new Date(),
          topicChoice: 'ask',
        }]);
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      setMessages(prev =>
        prev.map(m => m.id === userMsg.id ? { ...m, status: 'error' } : m)
      );
      setMessages(prev => [...prev, {
        id: `err_${Date.now()}`,
        text: isDE
          ? 'Verbindungsfehler. Bitte versuchen Sie es erneut.'
          : 'Connection error. Please try again.',
        sender: 'agent',
        timestamp: new Date(),
      }]);
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };

  // Opens a fresh InvestMenter Brain conversation. With a question it is
  // answered at once in the app language; without one the Brain greets and
  // asks for the language first.
  const startBrain = (question?: string) => {
    archiveCurrentConversation();
    setHandoffActive(false);
    lastPollTimestampRef.current = null;
    chatModeRef.current = 'brain';
    setCurrentView('chat');
    setActiveTab('home');
    setViewingConv(null);
    if (question) {
      brainLangPendingRef.current = false;
      chatLangRef.current = isDE ? 'de' : 'en';
      setTimeout(() => handleSend(question, { fresh: true }), 150);
      return;
    }
    chatLangRef.current = undefined;
    brainLangPendingRef.current = true;
    setMessages([{
      id: `agent_${Date.now()}`,
      text: 'Welcome to Asset Desk AI, your personal AI Wealth Co-Pilot from Asset Desk in Dubai.\nWillkommen bei der Asset Desk KI, Ihrem pers\u00f6nlichen AI Wealth Co-Pilot von Asset Desk in Dubai.\n\nPlease choose your language / Bitte w\u00e4hlen Sie Ihre Sprache:\n1. English\n2. Deutsch',
      sender: 'agent',
      timestamp: new Date(),
    }]);
  };

  // A topic from the list goes to the Brain as a question, answered directly.
  // It used to open the menu chat (which sends "Hello" and asks for the
  // language) and then send "Tell me about …" on top of it.
  const handleFAQClick = (faq: FAQItem) => {
    const title = isDE ? faq.titleDE : faq.titleEN;
    startBrain(isDE ? `Erzählen Sie mir mehr über: ${title}` : `Tell me about: ${title}`);
  };

  // Moves the live conversation into the history list and starts an empty one.
  const archiveCurrentConversation = () => {
    if (messages.length > 0) {
      const conv: Conversation = {
        id: currentConvIdRef.current,
        startedAt: messages[0].timestamp instanceof Date
          ? messages[0].timestamp.toISOString()
          : new Date(messages[0].timestamp).toISOString(),
        messages,
      };
      setConversations(prev => {
        const updated = [...prev, conv];
        saveConversations(investorEmail, updated);
        return updated;
      });
      setMessages([]);
      currentConvIdRef.current = `conv_${Date.now()}`;
    }
  };

  const openNewChat = () => {
    // Archive existing conversation before starting fresh
    archiveCurrentConversation();
    chatModeRef.current = undefined;
    brainLangPendingRef.current = false;
    chatLangRef.current = undefined;
    // Reset handoff state
    setHandoffActive(false);
    lastPollTimestampRef.current = null;
    setCurrentView('chat');
    setActiveTab('messages');
    setViewingConv(null);
    setTimeout(() => handleSend('Hello'), 100);
  };

  const goHome = () => {
    setCurrentView('home');
    setActiveTab('home');
    setViewingConv(null);
  };

  // ── Parse numbered choices for button rendering ───────────────
  const parseChoices = (text: string): { textBefore: string; choices: { label: string; value: string }[] } | null => {
    // ── Approach 1: Inline emoji keycap scan (handles all-on-one-line) ──
    // e.g. "Would you like: 1️⃣ Option A 2️⃣ Option B Please reply with 1 or 2."
    const emojiRe = /([1-9])\uFE0F\u20E3/g;
    const emojiMatches: { num: number; index: number; len: number }[] = [];
    let em: RegExpExecArray | null;
    while ((em = emojiRe.exec(text)) !== null) {
      emojiMatches.push({ num: parseInt(em[1]), index: em.index, len: em[0].length });
    }
    if (emojiMatches.length >= 2) {
      let sequential = true;
      for (let i = 0; i < emojiMatches.length; i++) {
        if (emojiMatches[i].num !== i + 1) { sequential = false; break; }
      }
      if (sequential) {
        const textBefore = text.slice(0, emojiMatches[0].index).trim();
        const inlineChoices: { label: string; value: string }[] = [];
        for (let i = 0; i < emojiMatches.length; i++) {
          const markerEnd = emojiMatches[i].index + emojiMatches[i].len;
          const end = i + 1 < emojiMatches.length ? emojiMatches[i + 1].index : text.length;
          let label = text.slice(markerEnd, end).trim();
          // Strip trailing instructions like "Please reply with 1 or 2."
          label = label.replace(/\s*[Pp]lease\s+reply\s+with\s+.*$/i, '').trim();
          label = label.replace(/\s*[Rr]eply\s+with\s+.*$/i, '').trim();
          inlineChoices.push({ label, value: String(emojiMatches[i].num) });
        }
        if (inlineChoices.length >= 2) {
          return { textBefore, choices: inlineChoices };
        }
      }
    }

    // ── Approach 2: Line-based parsing ──────────────────────────────
    // Handles multi-line: "1 for English\n2 for Deutsch" or "1️⃣ Option\n2️⃣ Option"
    const lines = text.split('\n');
    const choiceStartPattern = /^([1-9]\uFE0F\u20E3|[1-9][\.\):]?\s)/;
    const choiceForStart = /^([1-9])\s+for\s+(.+)/i;
    const choiceForEmbedded = /\b([1-9])\s+for\s+(\w[^,\n]*)/gi;

    const choices: { label: string; value: string }[] = [];
    const beforeLines: string[] = [];
    let foundChoices = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        if (!foundChoices) beforeLines.push(line);
        continue;
      }

      // Line starts with "N for X"
      const forStartMatch = trimmed.match(choiceForStart);
      if (forStartMatch) {
        foundChoices = true;
        choices.push({ label: `${forStartMatch[1]} for ${forStartMatch[2]}`, value: forStartMatch[1] });
        continue;
      }

      // Line starts with numbered bullet (1️⃣, 1., 1), 1 )
      if (choiceStartPattern.test(trimmed)) {
        foundChoices = true;
        const numMatch = trimmed.match(/^([1-9])/);
        choices.push({ label: trimmed, value: numMatch ? numMatch[1] : trimmed });
        continue;
      }

      // Line contains embedded "N for X" (e.g. bilingual: "Bitte: 1 for English")
      const embeddedMatches = Array.from(trimmed.matchAll(choiceForEmbedded));
      if (embeddedMatches.length > 0) {
        for (const m of embeddedMatches) {
          choices.push({ label: `${m[1]} for ${m[2].trim()}`, value: m[1] });
        }
        if (!foundChoices) {
          const prefix = trimmed.slice(0, trimmed.search(choiceForEmbedded)).trim();
          if (prefix) beforeLines.push(prefix);
        }
        foundChoices = true;
        continue;
      }

      if (!foundChoices) beforeLines.push(line);
    }

    return choices.length >= 2
      ? { textBefore: beforeLines.join('\n').trim(), choices }
      : null;
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hello Asset Desk Team, I need assistance.\n\nInvestor: ${investorName}\nEmail: ${investorEmail}`
    );
    window.location.href = `https://wa.me/971508418876?text=${msg}`;
  };

  const filteredFAQs = FAQ_ITEMS.filter(faq => {
    if (!searchQuery) return true;
    const title = isDE ? faq.titleDE : faq.titleEN;
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!isOpen) return null;

  // ============================================================
  // VOICE MESSAGE PLAYER (WhatsApp-style)
  // ============================================================
  const VoiceMessagePlayer: React.FC<{ audioUrl: string; duration: number; isUser: boolean }> = ({ audioUrl, duration, isUser }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const animFrameRef = useRef<number>(0);

    const togglePlay = () => {
      if (!audioRef.current) {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          setIsPlaying(false);
          setProgress(0);
          setCurrentTime(0);
          cancelAnimationFrame(animFrameRef.current);
        };

        audio.onerror = () => {
          setIsPlaying(false);
        };
      }

      if (isPlaying) {
        audioRef.current.pause();
        cancelAnimationFrame(animFrameRef.current);
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);

        const updateProgress = () => {
          if (audioRef.current) {
            const dur = audioRef.current.duration || duration || 1;
            setProgress((audioRef.current.currentTime / dur) * 100);
            setCurrentTime(Math.floor(audioRef.current.currentTime));
          }
          animFrameRef.current = requestAnimationFrame(updateProgress);
        };
        updateProgress();
      }
    };

    const displayTime = isPlaying ? currentTime : duration;
    const m = Math.floor(displayTime / 60);
    const s = displayTime % 60;
    const timeLabel = `${m}:${s.toString().padStart(2, '0')}`;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '180px' }}>
        <button
          onClick={togglePlay}
          style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: isUser ? 'rgba(255,255,255,0.25)' : '#D6B160',
            border: 'none', color: isUser ? '#fff' : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
        </button>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* Waveform bars */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', height: '24px' }}>
            {Array.from({ length: 20 }).map((_, i) => {
              const barProgress = (i / 20) * 100;
              const heights = [8, 14, 10, 18, 12, 20, 14, 8, 16, 12, 18, 10, 14, 20, 8, 16, 12, 18, 10, 14];
              return (
                <div
                  key={i}
                  style={{
                    width: '3px',
                    height: `${heights[i]}px`,
                    borderRadius: '2px',
                    background: barProgress <= progress
                      ? (isUser ? 'rgba(255,255,255,0.9)' : '#D6B160')
                      : (isUser ? 'rgba(255,255,255,0.35)' : '#d1d5db'),
                    transition: 'background 0.1s ease',
                  }}
                />
              );
            })}
          </div>
          <span style={{
            fontSize: '11px',
            color: isUser ? 'rgba(255,255,255,0.7)' : '#9ca3af',
          }}>
            {timeLabel}
          </span>
        </div>
      </div>
    );
  };

  // ============================================================
  // SHARED: Render messages list (used for live chat + past conv view)
  // ============================================================
  const renderMessagesList = (msgs: Message[], readOnly = false) => (
    <>
      {msgs.map((msg, idx) => {
        const isAgentLike = msg.sender === 'agent' || msg.sender === 'advisor' || msg.sender === 'system';
        const isLastAgentMsg = !readOnly && msg.sender === 'agent' && idx === msgs.length - 1;
        const isLastSystemMsg = !readOnly && msg.sender === 'system' && idx === msgs.length - 1;
        const parsed = (msg.sender === 'agent' || msg.sender === 'system') ? parseChoices(msg.text) : null;
        const showButtons = parsed && (isLastAgentMsg || isLastSystemMsg) && !isSending;
        const displayText = showButtons ? parsed.textBefore : msg.text;

        // System messages (handoff notifications) — centered, subtle
        if (msg.sender === 'system' && (msg.type === 'inactivity_warning' || msg.type === 'auto_close')) {
          const sysParsed = parseChoices(msg.text);
          const sysShowButtons = sysParsed && idx === msgs.length - 1 && !isSending;
          return (
            <div key={msg.id}>
              <div style={{
                display: 'flex', justifyContent: 'center', marginBottom: sysShowButtons ? '8px' : '12px',
              }}>
                <div style={{
                  maxWidth: '85%', padding: '10px 16px', borderRadius: '12px',
                  background: '#FEF3C7', border: '1px solid #F59E0B',
                  color: '#92400E', fontSize: '13px', lineHeight: '1.5', textAlign: 'center',
                }}>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{sysShowButtons ? sysParsed.textBefore : msg.text}</p>
                  <span style={{ fontSize: '11px', color: '#B45309', marginTop: '4px', display: 'block' }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
              {sysShowButtons && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '6px',
                  marginLeft: '36px', marginBottom: '12px', maxWidth: '85%',
                }}>
                  {sysParsed.choices.map((choice, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(choice.value)}
                      style={{
                        padding: '10px 14px', background: '#fff',
                        border: '1.5px solid #F59E0B', borderRadius: '12px',
                        fontSize: '14px', color: '#92400E', cursor: 'pointer',
                        textAlign: 'left', fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500, transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = '#FFFBEB'; }}
                      onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = '#fff'; }}
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // Advisor message avatar + badge
        const isAdvisor = msg.sender === 'advisor';
        const avatarBg = isAdvisor
          ? 'linear-gradient(135deg, #3B82F6, #1D4ED8)'
          : 'linear-gradient(135deg, #D6B160, #B8941F)';
        const avatarLabel = isAdvisor ? (msg.senderName?.[0]?.toUpperCase() || 'A') : 'IM';

        return (
          <div key={msg.id}>
            <div style={{
              display: 'flex',
              justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: showButtons ? '8px' : '12px',
              gap: '8px',
            }}>
              {isAgentLike && msg.sender !== 'system' && (
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: avatarBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: '2px',
                }}>
                  <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>{avatarLabel}</span>
                </div>
              )}
              <div style={{ maxWidth: '78%' }}>
                {/* Advisor name badge */}
                {isAdvisor && (
                  <div style={{
                    fontSize: '11px', fontWeight: 600, color: '#3B82F6',
                    marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: '8px',
                      background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '10px',
                    }}>
                      ADVISOR
                    </span>
                    {msg.senderName || (appLanguage() === 'de' ? 'Berater' : 'Advisor')}
                  </div>
                )}
                <div style={{
                  padding: msg.audioUrl ? '8px 12px' : '10px 14px',
                  borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.sender === 'user' ? '#D6B160' : isAdvisor ? '#EFF6FF' : '#ffffff',
                  color: msg.sender === 'user' ? '#fff' : '#1f2937',
                  boxShadow: isAdvisor ? '0 1px 4px rgba(59,130,246,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
                  fontSize: '14px', lineHeight: '1.5',
                  border: isAdvisor ? '1px solid #BFDBFE' : 'none',
                  minWidth: msg.audioUrl ? '220px' : undefined,
                }}>
                  {msg.audioUrl ? (
                    <VoiceMessagePlayer
                      audioUrl={msg.audioUrl}
                      duration={msg.audioDuration || 0}
                      isUser={msg.sender === 'user'}
                    />
                  ) : (
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {displayText}
                    </p>
                  )}
                  {!msg.audioUrl && (
                    <span style={{
                      fontSize: '11px', display: 'block', marginTop: '4px',
                      color: msg.sender === 'user' ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                    }}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {msg.status === 'error' && ' · Failed'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {msg.topicChoice === 'ask' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginLeft: '36px', marginBottom: '12px', maxWidth: '85%' }}>
                {(['realestate', 'pm'] as const).map((tp) => (
                  <button key={tp} type="button" onClick={() => chooseHandoffTopic(msg.id, tp)}
                    style={{ padding: '9px 14px', borderRadius: '18px', border: '1.5px solid #D6B160', background: '#fff', color: '#1B1B1B', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                    {tp === 'realestate'
                      ? (isDE ? 'Meine Immobilie / Investment' : 'My property / real estate')
                      : (isDE ? 'Hausverwaltung' : 'Property management')}
                  </button>
                ))}
              </div>
            )}

            {msg.offers && msg.offers.length > 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '8px',
                marginLeft: '36px', marginBottom: '12px', maxWidth: '85%',
              }}>
                {msg.offers.map((o) => {
                  const name = o.title || o.propertyType || (isDE ? 'Einheit' : 'Unit');
                  const meta = [
                    o.location,
                    o.bedrooms != null ? (o.bedrooms === 0 ? 'Studio' : `${o.bedrooms} BR`) : null,
                    o.sizeSqft ? `${Math.round(o.sizeSqft).toLocaleString(isDE ? 'de-DE' : 'en-AE')} sqft` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <div key={o.id} style={{
                      display: 'flex', gap: '10px', alignItems: 'stretch',
                      background: '#fff', border: '1.5px solid #D6B160', borderRadius: '12px',
                      overflow: 'hidden', fontFamily: 'Montserrat, sans-serif',
                    }}>
                      {o.photoUrl && (
                        <img src={o.photoUrl} alt="" loading="lazy"
                          style={{ width: '84px', minHeight: '84px', objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <div style={{ padding: '10px 12px 10px ' + (o.photoUrl ? '0' : '12px'), minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>{name}</div>
                        {meta && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{meta}</div>}
                        <div style={{ fontWeight: 700, fontSize: '14px', color: '#000', marginTop: '4px' }}>
                          {o.askingPrice ? `AED ${Math.round(o.askingPrice).toLocaleString(isDE ? 'de-DE' : 'en-AE')}` : ''}
                          {o.isDistress && o.discountPercent ? (
                            <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 700, color: '#000', background: '#D6B160', borderRadius: '6px', padding: '1px 6px' }}>
                              −{o.discountPercent}%
                            </span>
                          ) : null}
                        </div>
                        {offerFactLines(o.facts, isDE).map((fl) => (
                          <div key={fl.text} style={{
                            marginTop: '4px', fontSize: '11.5px', fontWeight: 600, lineHeight: 1.35,
                            color: '#000', ...(fl.good ? { background: 'rgba(214,177,96,0.28)', borderRadius: '4px', padding: '1px 5px', display: 'inline-block' } : { opacity: 0.75 }),
                          }}>
                            {fl.text}
                          </div>
                        ))}
                        {!readOnly && (
                          <button
                            onClick={() => handleSend(isDE
                              ? `Ich interessiere mich für ${name}${o.location ? ` (${o.location})` : ''}.`
                              : `I am interested in ${name}${o.location ? ` (${o.location})` : ''}.`)}
                            disabled={isSending}
                            style={{
                              marginTop: '8px', padding: '6px 12px', borderRadius: '999px',
                              background: '#000', color: '#fff', border: 'none', cursor: 'pointer',
                              fontSize: '12px', fontWeight: 700, fontFamily: 'Montserrat, sans-serif',
                            }}
                          >
                            {isDE ? 'Interessiert mich' : 'I am interested'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showButtons && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '6px',
                marginLeft: '36px', marginBottom: '12px', maxWidth: '85%',
              }}>
                {parsed.choices.map((choice, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(choice.value)}
                    style={{
                      padding: '10px 14px',
                      background: '#fff',
                      border: '1.5px solid #D6B160',
                      borderRadius: '12px',
                      fontSize: '14px',
                      color: '#1a1a2e',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 500,
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.background = '#faf5e8';
                      (e.target as HTMLButtonElement).style.borderColor = '#B8941F';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.background = '#fff';
                      (e.target as HTMLButtonElement).style.borderColor = '#D6B160';
                    }}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  // ============================================================
  // LIVE CHAT VIEW
  // ============================================================
  const renderChatView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', borderBottom: '1px solid #e5e7eb',
        flexShrink: 0, background: '#fff',
      }}>
        <button onClick={goHome} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}>
          <ArrowLeft size={20} color="#374151" />
        </button>
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #D6B160, #B8941F)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}>IM</span>
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
            {handoffActive ? (isDE ? 'Live-Berater' : 'Live Advisor') : 'Asset Desk'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: handoffActive ? '#3B82F6' : '#22c55e',
              animation: handoffActive ? 'pulse 1.5s infinite' : 'none',
            }} />
            <span style={{ fontSize: '12px', color: handoffActive ? '#3B82F6' : '#6b7280', fontWeight: handoffActive ? 600 : 400 }}>
              {handoffActive ? (isDE ? 'Berater verbunden' : 'Advisor connected') : 'Online'}
            </span>
          </div>
        </div>
      </div>

      {/* Handoff banner */}
      {handoffActive && (
        <div style={{
          padding: '8px 16px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '12px', color: '#1D4ED8',
        }}>
          <span>{isDE ? 'Sie sind mit einem Berater verbunden' : 'You are connected with an advisor'}</span>
          <button
            onClick={() => handleSend(isDE ? 'Gespräch beenden' : 'End conversation')}
            style={{
              background: 'none', border: '1px solid #93C5FD', borderRadius: '6px',
              padding: '2px 8px', fontSize: '11px', color: '#1D4ED8', cursor: 'pointer',
            }}
          >
            {isDE ? 'Beenden' : 'End chat'}
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        padding: '16px', background: '#f9fafb', minHeight: 0,
        WebkitOverflowScrolling: 'touch' as any,
      }}>
        {renderMessagesList(messages)}
        {isTyping && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #D6B160, #B8941F)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>IM</span>
            </div>
            <div style={{
              padding: '10px 14px', borderRadius: '16px 16px 16px 4px',
              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              <div className="cw-typing-dots"><span /><span /><span /></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '12px 12px 16px',
        borderTop: '1px solid #e5e7eb', background: '#ffffff',
        flexShrink: 0, minHeight: '56px', boxSizing: 'border-box' as any,
      }}>
        {isRecording ? (
          /* ── Recording state: WhatsApp-style hold-to-record ── */
          <>
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 14px',
              background: '#fef2f2', borderRadius: '20px',
              border: '1.5px solid #fca5a5',
              transform: `translateX(${slideOffset}px)`,
              transition: slideOffset === 0 ? 'transform 0.2s ease' : 'none',
              opacity: Math.max(0.3, 1 + slideOffset / 150),
            }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: '#ef4444',
                animation: 'cw-pulse 1.5s ease-in-out infinite',
                flexShrink: 0,
              }} />
              <span style={{
                fontSize: '14px', fontWeight: 600, fontFamily: 'monospace',
                color: recordingTime >= 105 ? '#fff' : '#dc2626',
                background: recordingTime >= 105 ? '#dc2626' : 'transparent',
                padding: recordingTime >= 105 ? '2px 6px' : '0',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
              }}>
                {formatRecordingTime(recordingTime)}{recordingTime >= 105 ? ` / ${formatRecordingTime(120)}` : ''}
              </span>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '2px', overflow: 'hidden' }}>
                {Array.from({ length: 24 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '3px',
                      height: `${8 + Math.random() * 14}px`,
                      borderRadius: '2px',
                      background: '#ef4444',
                      opacity: 0.5 + Math.random() * 0.5,
                      animation: `cwBounce ${0.8 + Math.random() * 0.6}s ease-in-out infinite`,
                      animationDelay: `${i * 0.05}s`,
                    }}
                  />
                ))}
              </div>
              <span style={{
                fontSize: '12px', color: '#9ca3af',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {isDE ? '< Wischen zum Abbrechen' : '< Slide to cancel'}
              </span>
            </div>
            {/* Mic button stays visible while held */}
            <div
              style={{
                width: '44px', height: '44px', minWidth: '44px',
                borderRadius: '50%',
                background: '#ef4444',
                border: 'none', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                animation: 'cw-pulse 1.5s ease-in-out infinite',
              }}
            >
              <Mic size={20} />
            </div>
          </>
        ) : (
          /* ── Normal input state ── */
          <>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && inputText.trim() && !isSending) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={isDE ? 'Nachricht eingeben...' : 'Type a message...'}
              disabled={isSending}
              autoComplete="off"
              autoCapitalize="sentences"
              enterKeyHint="send"
              style={{
                flex: 1, padding: '10px 14px',
                border: '1.5px solid #D6B160', borderRadius: '20px',
                fontSize: '16px', outline: 'none', background: '#f9fafb',
                fontFamily: 'Montserrat, sans-serif',
                WebkitAppearance: 'none' as any, minWidth: 0,
                transition: 'border-color 0.2s ease',
              }}
            />
            {inputText.trim() ? (
              <button
                onClick={() => handleSend()}
                disabled={isSending}
                style={{
                  width: '40px', height: '40px', minWidth: '40px',
                  borderRadius: '50%',
                  background: !isSending ? '#D6B160' : '#d1d5db',
                  border: 'none', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: !isSending ? 'pointer' : 'default',
                  flexShrink: 0,
                }}
              >
                <Send size={18} />
              </button>
            ) : (
              <button
                onTouchStart={handleMicTouchStart}
                onMouseDown={handleMicMouseDown}
                disabled={isSending}
                style={{
                  width: '40px', height: '40px', minWidth: '40px',
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  border: '1.5px solid #e5e7eb',
                  color: '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'none',
                }}
                title={isDE ? 'Halten zum Aufnehmen' : 'Hold to record'}
              >
                <Mic size={18} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  // ============================================================
  // PAST CONVERSATION VIEW (read-only)
  // ============================================================
  const renderConvView = () => {
    if (!viewingConv) return null;
    const dateLabel = new Date(viewingConv.startedAt).toLocaleDateString(isDE ? 'de-DE' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px', borderBottom: '1px solid #e5e7eb',
          flexShrink: 0, background: '#fff',
        }}>
          <button
            onClick={() => { setCurrentView('home'); setActiveTab('messages'); setViewingConv(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
          >
            <ArrowLeft size={20} color="#374151" />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
              {isDE ? 'Frühere Konversation' : 'Past Conversation'}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{dateLabel}</div>
          </div>
          <button
            onClick={openNewChat}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 12px', background: '#D6B160', color: '#fff',
              border: 'none', borderRadius: '16px', fontSize: '13px',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
            }}
          >
            <Plus size={14} />
            {isDE ? 'Neu' : 'New'}
          </button>
        </div>

        {/* Past messages (read-only) */}
        <div style={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden',
          padding: '16px', background: '#f9fafb', minHeight: 0,
          WebkitOverflowScrolling: 'touch' as any,
        }}>
          {renderMessagesList(viewingConv.messages, true)}
          <div ref={messagesEndRef} />
        </div>

        {/* Footer prompt to start new chat */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid #e5e7eb',
          background: '#fff', textAlign: 'center',
        }}>
          <button
            onClick={openNewChat}
            style={{
              padding: '10px 24px', background: '#D6B160', color: '#fff',
              border: 'none', borderRadius: '20px', fontSize: '14px',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {isDE ? '+ Neue Konversation starten' : '+ Start New Conversation'}
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // HOME VIEW
  // ============================================================
  const renderHomeView = () => (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        padding: '24px 20px 20px', color: '#fff',
      }}>
        {/* divs, not h2/p: index.css forces a dark heading colour with
            !important, which made the greeting near-invisible on black. */}
        <div style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>
          {isDE ? `Hallo ${firstName}` : `Hi ${firstName}`}
        </div>
        <div style={{ margin: 0, fontSize: '15px', color: 'rgba(255,255,255,0.85)' }}>
          {isDE ? 'Wie können wir helfen?' : 'How can we help?'}
        </div>
      </div>

      <div style={{ padding: '16px 16px 20px' }}>
        {/* Asset Desk Brain — the advisor, first */}
        <button onClick={() => startBrain()} style={{
          display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
          padding: '16px', marginBottom: '10px', borderRadius: '14px',
          background: '#000', border: '1.5px solid #D6B160', color: '#fff',
          fontFamily: 'Montserrat, sans-serif',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#D6B160' }}>
                AI Wealth Co-Pilot
              </div>
              <div style={{ fontSize: '16px', fontWeight: 800, marginTop: '2px' }}>{isDE ? 'Asset Desk KI' : 'Asset Desk AI'}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '4px', lineHeight: 1.4 }}>
                {isDE ? 'Analysiert. Erkennt. Warnt. Empfiehlt. Sie entscheiden. Mit echten DLD-Zahlen.' : 'Analyses. Detects. Warns. Recommends. You decide. With real DLD numbers.'}
              </div>
            </div>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
              background: '#D6B160', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ChevronRight size={20} color="#000" />
            </div>
          </div>
        </button>

        {/* Hook questions: answered by the Brain at once */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
          {(isDE
            ? ['Was bleibt nach allen Kosten wirklich übrig?', 'Wie sicher ist mein Geld in Dubai?', 'Welche Einheiten passen zu meinem Budget?']
            : ['What really remains after all costs?', 'How safe is my money in Dubai?', 'Which units fit my budget?']
          ).map(q => (
            <button key={q} onClick={() => startBrain(q)} style={{
              width: '100%', textAlign: 'left', cursor: 'pointer',
              padding: '10px 14px', borderRadius: '10px',
              background: '#fff', border: '1px solid #D6B160', color: '#000',
              fontSize: '13.5px', fontWeight: 600, fontFamily: 'Montserrat, sans-serif',
            }}>
              {q}
            </button>
          ))}
        </div>

        {/* Ask a Question — the general assistant: account, documents, team */}
        <button onClick={openNewChat} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '14px 16px', background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: '12px',
          cursor: 'pointer', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
              {isDE ? 'Frage stellen' : 'Ask a question'}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
              {isDE ? 'Konto, Dokumente oder mit dem Team sprechen' : 'Account, documents or talk to the team'}
            </div>
          </div>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #D6B160, #B8941F)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageSquare size={20} color="#fff" />
          </div>
        </button>

        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 14px', background: '#f3f4f6', borderRadius: '8px', marginBottom: '16px',
        }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isDE ? 'Hilfe suchen...' : 'Search for help...'}
            style={{
              border: 'none', background: 'transparent', outline: 'none',
              fontSize: '14px', flex: 1, fontFamily: 'Montserrat, sans-serif',
            }}
          />
        </div>

        {/* FAQ List */}
        {filteredFAQs.map(faq => (
          <button key={faq.id} onClick={() => handleFAQClick(faq)} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '12px 4px', background: 'transparent', border: 'none',
            borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: '#6b7280' }}>{faq.icon}</span>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                {isDE ? faq.titleDE : faq.titleEN}
              </span>
            </div>
            <ChevronRight size={16} color="#9ca3af" />
          </button>
        ))}
      </div>
    </div>
  );

  // ============================================================
  // MESSAGES TAB — past conversations list
  // ============================================================
  const renderMessagesView = () => {
    if (currentView === 'chat') return renderChatView();
    if (currentView === 'conv-view') return renderConvView();

    // Include current live session in the list if it has messages
    const hasActiveLiveChat = messages.length > 0;

    // Sort: newest first
    const allConvs = [...conversations].reverse();

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header with New Chat button */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 16px 12px', flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
            {isDE ? 'Nachrichten' : 'Messages'}
          </h3>
          <button
            onClick={openNewChat}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '6px 12px', background: '#D6B160', color: '#fff',
              border: 'none', borderRadius: '16px', fontSize: '13px',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
            }}
          >
            <Plus size={14} />
            {isDE ? 'Neu' : 'New chat'}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
          {/* Active live chat (if any) */}
          {hasActiveLiveChat && (
            <button
              onClick={() => { setCurrentView('chat'); setActiveTab('messages'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                width: '100%', padding: '12px', background: '#faf5e8',
                border: '1.5px solid #D6B160', borderRadius: '10px',
                cursor: 'pointer', textAlign: 'left', marginBottom: '8px',
              }}
            >
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #D6B160, #B8941F)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>IM</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Asset Desk</span>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {isDE ? 'Aktiv' : 'Active'}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px', color: '#6b7280',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {stripActions(messages[messages.length - 1]?.text || '').substring(0, 50)}
                </div>
              </div>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
            </button>
          )}

          {/* Past conversations */}
          {allConvs.length === 0 && !hasActiveLiveChat && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: '#f3f4f6', margin: '0 auto 12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MessageSquare size={24} color="#9ca3af" />
              </div>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                {isDE ? 'Noch keine Nachrichten' : 'No messages yet'}
              </p>
              <button onClick={openNewChat} style={{
                padding: '10px 24px', background: '#D6B160', color: '#fff',
                border: 'none', borderRadius: '20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}>
                {isDE ? 'Frage stellen' : 'Ask a question'}
              </button>
            </div>
          )}

          {allConvs.map(conv => {
            const lastMsg = conv.messages[conv.messages.length - 1];
            const preview = stripActions(lastMsg?.text || '').substring(0, 50);
            const dateLabel = new Date(conv.startedAt).toLocaleDateString(isDE ? 'de-DE' : 'en-GB', {
              day: 'numeric', month: 'short',
            });
            const timeLabel = new Date(conv.startedAt).toLocaleTimeString([], {
              hour: '2-digit', minute: '2-digit',
            });

            return (
              <button
                key={conv.id}
                onClick={() => {
                  setViewingConv(conv);
                  setCurrentView('conv-view');
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  width: '100%', padding: '12px', background: '#fff',
                  border: '1px solid #e5e7eb', borderRadius: '10px',
                  cursor: 'pointer', textAlign: 'left', marginBottom: '8px',
                }}
              >
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #D6B160, #B8941F)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>IM</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Asset Desk</span>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>{dateLabel} {timeLabel}</span>
                  </div>
                  <div style={{
                    fontSize: '13px', color: '#6b7280',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {preview || '...'}
                  </div>
                </div>
                <ChevronRight size={14} color="#9ca3af" />
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  const totalConvCount = conversations.length + (messages.length > 0 ? 1 : 0);

  return (
    <div className="cw-overlay">
      <div className="cw-panel">
        {/* Top Bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>✨</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>Asset Desk</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>{isDE ? '🇩🇪' : '🇬🇧'}</span>
            <button onClick={onToggle} style={{
              background: 'rgba(255,255,255,0.1)', border: 'none',
              borderRadius: '50%', width: '28px', height: '28px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
              <X size={16} color="#fff" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div style={{
          flex: 1, overflow: currentView === 'chat' || currentView === 'conv-view' ? 'hidden' : 'auto',
          minHeight: 0, display: 'flex', flexDirection: 'column',
        }}>
          {activeTab === 'home' && currentView === 'home' && renderHomeView()}
          {activeTab === 'messages' && renderMessagesView()}
          {activeTab === 'home' && currentView === 'chat' && renderChatView()}
        </div>

        {/* Bottom Tab Bar */}
        {currentView !== 'chat' && currentView !== 'conv-view' && (
          <div style={{
            display: 'flex', borderTop: '1px solid #e5e7eb',
            background: '#fff', flexShrink: 0,
          }}>
            <button
              onClick={() => { setActiveTab('home'); setCurrentView('home'); setViewingConv(null); }}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '2px', padding: '8px 0',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: activeTab === 'home' ? '#D6B160' : '#9ca3af',
                borderBottom: activeTab === 'home' ? '2px solid #D6B160' : '2px solid transparent',
              }}
            >
              <Home size={18} />
              <span style={{ fontSize: '11px', fontWeight: 500 }}>{appLanguage() === 'de' ? 'Start' : 'Home'}</span>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '2px', padding: '8px 0',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: activeTab === 'messages' ? '#D6B160' : '#9ca3af',
                borderBottom: activeTab === 'messages' ? '2px solid #D6B160' : '2px solid transparent',
                position: 'relative',
              }}
            >
              <MessageSquare size={18} />
              <span style={{ fontSize: '11px', fontWeight: 500 }}>{isDE ? 'Nachrichten' : 'Messages'}</span>
              {totalConvCount > 0 && (
                <div style={{
                  position: 'absolute', top: '4px', right: 'calc(50% - 20px)',
                  width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444',
                }} />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
